import React, { useState, useEffect } from 'react';
import { Mail, Plus, Copy, Trash2, RefreshCw, CheckCircle, XCircle, Clock, TrendingUp } from 'lucide-react';
import { invitationService } from '../../../services/invitationService';

import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import InviteUserModal from '../../../pages/settings/components/InviteUserModal';
import Icon from '../../../components/AppIcon';


export default function InviteManagementSection({ onStatsUpdate }) {
  const { profile } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    try {
      setLoading(true);
      const data = await invitationService?.getInvitationsForTenant(profile?.tenant_id);
      setInvitations(data || []);
      
      const pendingCount = data?.filter(inv => inv?.status === 'pending')?.length || 0;
      onStatsUpdate?.({ pendingInvites: pendingCount });
    } catch (error) {
      console.error('Error loading invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyInviteLink = (token) => {
    const inviteLink = `${window?.location?.origin}/signup?invite=${token}`;
    navigator?.clipboard?.writeText(inviteLink);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleResendInvite = async (invitationId) => {
    try {
      await invitationService?.resendInvitation(invitationId);
      await loadInvitations();
      alert('Invitation resent successfully!');
    } catch (error) {
      console.error('Error resending invitation:', error);
      alert('Failed to resend invitation: ' + error?.message);
    }
  };

  const handleCancelInvite = async (invitationId) => {
    if (!confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }

    try {
      await invitationService?.cancelInvitation(invitationId);
      await loadInvitations();
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      alert('Failed to cancel invitation: ' + error?.message);
    }
  };

  const handleDeleteInvite = async (invitationId) => {
    if (!confirm('Are you sure you want to delete this invitation?')) {
      return;
    }

    try {
      await invitationService?.deleteInvitation(invitationId);
      await loadInvitations();
    } catch (error) {
      console.error('Error deleting invitation:', error);
      alert('Failed to delete invitation: ' + error?.message);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      accepted: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Accepted' },
      declined: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Declined' },
      expired: { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'Expired' },
      cancelled: { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'Cancelled' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    const Icon = config?.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon className="w-3 h-3" />
        {config?.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const stats = {
    total: invitations?.length,
    pending: invitations?.filter(inv => inv?.status === 'pending')?.length,
    accepted: invitations?.filter(inv => inv?.status === 'accepted')?.length,
    acceptanceRate: invitations?.length > 0 
      ? Math.round((invitations?.filter(inv => inv?.status === 'accepted')?.length / invitations?.length) * 100)
      : 0
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Invite Code Management</h2>
          <p className="text-sm text-gray-600 mt-1">
            Generate, track, and manage organization invitations
          </p>
        </div>
        <Button onClick={() => setShowInviteModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Send Invitation
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Invites</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.total}</p>
            </div>
            <Mail className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{stats?.pending}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Accepted</p>
              <p className="text-2xl font-bold text-green-600">{stats?.accepted}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Acceptance Rate</p>
              <p className="text-2xl font-bold text-purple-600">{stats?.acceptanceRate}%</p>
            </div>
            <TrendingUp className="w-8 h-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Invitations Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expires
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      <span className="ml-2">Loading invitations...</span>
                    </div>
                  </td>
                </tr>
              ) : invitations?.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <Mail className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>No invitations sent yet</p>
                    <Button
                      size="sm"
                      className="mt-3"
                      onClick={() => setShowInviteModal(true)}
                    >
                      Send First Invitation
                    </Button>
                  </td>
                </tr>
              ) : (
                invitations?.map((invitation) => (
                  <tr key={invitation?.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Mail className="w-5 h-5 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">{invitation?.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{invitation?.role?.display_name || 'N/A'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(invitation?.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(invitation?.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(invitation?.expiresAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {invitation?.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCopyInviteLink(invitation?.invitationToken)}
                              title="Copy invite link"
                            >
                              {copiedToken === invitation?.invitationToken ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleResendInvite(invitation?.id)}
                              title="Resend invitation"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCancelInvite(invitation?.id)}
                              className="text-error hover:bg-error/10"
                              title="Cancel invitation"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {invitation?.status !== 'pending' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteInvite(invitation?.id)}
                            className="text-error hover:bg-error/10"
                            title="Delete invitation"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invite Modal */}
      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        tenantId={profile?.tenant_id}
        onSuccess={loadInvitations}
      />
    </div>
  );
}
